import { Injectable } from '@angular/core';
import {
  LoginResponse,
  PersonaConfig,
  PersonaPadlockError,
  RefreshAccessTokenConfig,
} from '@technologyfromsage/persona-padlock';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfig } from '®shared/config';
import { ErrorReportingService } from '®shared/reporting/error-reporting.service';

import { AuthenticationFailedException } from './authentication-failed-exception';
import { PersonaPadlockService } from './persona-padlock.service';
import {
  PersonaUser,
  createPersonaUserFromPkceLogin,
  isPersonaUser,
} from './persona-user';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

const USER_SSO_TIMEOUT_SECONDS = 5 * 60 * 1000;

@Injectable()
export class PersonaPkceSharedService {
  constructor(
    private appConfig: AppConfig,
    private errorReportingService: ErrorReportingService,
    private personaPadlockService: PersonaPadlockService,
    private userScopesService: UserScopesService,
  ) {}

  public throwAuthenticationFailedException(
    error: PersonaPadlockError,
  ): Observable<never> {
    this.errorReportingService.reportToInstana(error);
    return throwError(
      new AuthenticationFailedException(
        (error && error.message) || 'Unknown authentication error',
      ),
    );
  }

  public getPersonaConfig(authProvider: string): PersonaConfig {
    return {
      clientId: this.appConfig.oauthAppId,
      personaOrigin: this.appConfig.personaOrigin,
      provider: authProvider,
      timeoutMilliseconds: USER_SSO_TIMEOUT_SECONDS,
    };
  }

  public getAuthProvider(isTalisLogin: boolean): string {
    return isTalisLogin
      ? this.appConfig.talisLoginProvider
      : this.appConfig.tenant.shortCode;
  }

  public getCurrentToken(
    silentAuthenticationFn: () => Observable<LoginResponse>,
  ): Observable<User> {
    return silentAuthenticationFn().pipe(
      map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
      map(personaUser => {
        if (!this.userScopesService.isAuthorizedAsMemberAtTenant(personaUser)) {
          throw new AuthenticationFailedException(
            'Persona User from another tenancy',
          );
        }
        return personaUser;
      }),
      catchError(error => this.throwAuthenticationFailedException(error)),
    );
  }

  public refreshToken(currentUser: User): Observable<User> {
    if (!isPersonaUser(currentUser)) {
      return throwError(
        new AuthenticationFailedException('Not a persona user'),
      );
    }

    const refreshTokenConfig: RefreshAccessTokenConfig = {
      refreshToken: currentUser.refreshToken,
      personaOrigin: this.appConfig.personaOrigin,
      userGuid: currentUser.guid,
    };

    return this.personaPadlockService
      .refreshAccessToken(refreshTokenConfig)
      .pipe(
        map(
          loginResponse =>
            new PersonaUser({
              guid: currentUser.guid,
              oauth: {
                access_token: loginResponse.access_token,
                refresh_token: loginResponse.refresh_token,
                scope: currentUser.scopes,
                token_type: 'bearer',
              },
              profile: currentUser.profile,
              active_pid: currentUser.activePid,
            }),
        ),
        catchError(error => this.throwAuthenticationFailedException(error)),
      );
  }
}
